import React, { useContext, useEffect, useMemo, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useBlogContext } from "../../../../BlogContext.jsx";
import { UserContext } from "../../../../UserContext.js";
import { normalizeBlogTags } from "../../../../utils/blogUtils";

const EMPTY_FORM = {
  title: "",
  subtitle: "",
  description: "",
  userName: "",
  imageUrl: "",
};

const AddBlogForm = ({ editData = null, isEdit = false, onClose }) => {
  const { addBlog } = useBlogContext();
  const { user } = useContext(UserContext);

  const allTags = useMemo(
    () => [
      "Health",
      "Wealth",
      "Nutrition",
      "Fitness",
      "Mental Health",
      "Wellness",
      "Exercise",
      "Meditation",
      "Yoga",
      "Stress Management",
      "Mindfulness",
      "Sleep",
      "Cardio",
      "Diet",
      "Self-Care",
      "Personal Finance",
      "Investment",
      "Budgeting",
      "Financial Planning",
      "Passive Income",
      "Saving Money",
      "Retirement",
      "Healthy Living",
      "Lifestyle",
      "Work-Life Balance",
    ],
    []
  );

  const [inputValue, setInputValue] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ");

  useEffect(() => {
    if (editData) {
      setFormData({
        title: editData.title || "",
        subtitle: editData.subtitle || "",
        description: editData.description || "",
        userName: editData.username || fullName,
        imageUrl: editData.image_url || "",
      });
      setSelectedTags(normalizeBlogTags(editData));
      return;
    }

    setFormData({
      ...EMPTY_FORM,
      userName: fullName,
    });
    setSelectedTags([]);
  }, [editData, fullName]);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleQuillChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      description: value,
      userName: fullName,
    }));
  };

  const handleTagInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setSuggestions(
      allTags
        .filter(
          (tag) =>
            tag.toLowerCase().includes(value.toLowerCase()) &&
            !selectedTags.includes(tag)
        )
        .slice(0, 5)
    );
  };

  const addTag = (tag) => {
    if (selectedTags.length >= 5 || selectedTags.includes(tag)) return;
    setSelectedTags((prev) => [...prev, tag]);
    setInputValue("");
    setSuggestions([]);
  };

  const removeTag = (tagToRemove) => {
    setSelectedTags((prev) => prev.filter((tag) => tag !== tagToRemove));
  };

  const resetForm = () => {
    setFormData({
      ...EMPTY_FORM,
      userName: fullName,
    });
    setSelectedTags([]);
    setInputValue("");
    setSuggestions([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await addBlog(formData, selectedTags);
      resetForm();

      if (onClose) onClose();
    } catch (error) {
      console.error("Failed to create blog:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={
        isEdit
          ? "fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
          : ""
      }
    >
      <form
        onSubmit={handleSubmit}
        className={`bg-white p-6 rounded-xl shadow ${
          isEdit ? "w-full max-w-lg relative" : "mb-6"
        }`}
      >
        {isEdit && (
          <button
            onClick={onClose}
            type="button"
            className="absolute top-2 right-2 text-xl"
          >
            x
          </button>
        )}
        {formData.imageUrl && (
          <img
            src={formData.imageUrl}
            alt="Blog preview"
            className="mt-3 rounded max-h-60 object-cover w-full border"
          />
        )}
        <h2 className="text-xl font-semibold mb-4">
          {isEdit ? "Edit Blog" : "Publish Blog"}
        </h2>

        <div className="flex items-start gap-4 sm:col-span-2">
          <button
            type="button"
            className="w-8 h-8 border border-gray-400 rounded-full flex items-center justify-center mt-1"
          >
            <span className="text-lg text-gray-500">+</span>
          </button>

          <div className="flex flex-col w-full">
            <input
              type="text"
              name="title"
              placeholder="Title"
              value={formData.title}
              onChange={handleChange}
              className="text-4xl font-serif font-medium text-gray-700 placeholder-gray-400 outline-none w-full"
              required
            />
            <input
              type="text"
              name="subtitle"
              placeholder="Subtitle"
              value={formData.subtitle}
              onChange={handleChange}
              className="mt-2 text-lg font-serif text-gray-600 placeholder-gray-400 outline-none w-full"
            />

            <div className="sm:col-span-2 mt-4">
              <ReactQuill
                value={formData.description}
                onChange={handleQuillChange}
                className="bg-white"
                placeholder="Tell your story..."
              />
            </div>

            <div className="mb-4 w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Image URL (optional)
              </label>
              <input
                type="text"
                name="imageUrl"
                placeholder="Paste image URL..."
                value={formData.imageUrl}
                onChange={handleChange}
                className="w-full border border-gray-300 p-2 rounded"
              />
            </div>

            <div className="w-full max-w-md flex flex-col justify-start items-start mt-3 sm:col-span-2">
              <div className="mb-2 text-gray-700 font-medium">
                Add up to 5 Tags
              </div>
              <div className="border p-2 rounded relative">
                <div className="flex flex-wrap gap-2">
                  {selectedTags.map((tag) => (
                    <div
                      key={tag}
                      className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm flex items-center"
                    >
                      {tag}
                      <button
                        onClick={() => removeTag(tag)}
                        type="button"
                        className="ml-1 text-red-500 hover:text-red-700"
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                  <input
                    value={inputValue}
                    onChange={handleTagInputChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const trimmedTag = inputValue.trim();
                        if (trimmedTag) addTag(trimmedTag);
                      }
                    }}
                    className="flex-grow outline-none p-1"
                    placeholder="Type a tag..."
                  />
                </div>
                {suggestions.length > 0 && (
                  <div className="border mt-1 rounded shadow bg-white absolute z-10 w-full left-0">
                    {suggestions.map((tag) => (
                      <div
                        key={tag}
                        onClick={() => addTag(tag)}
                        className="p-2 hover:bg-gray-100 cursor-pointer"
                      >
                        {tag}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="mt-4 mx-auto flex items-center justify-center bg-[#54B0AF] text-white px-4 py-2 rounded hover:bg-blue-700 transition disabled:opacity-70"
        >
          {isSubmitting
            ? "Publishing..."
            : isEdit
              ? "Publish Blog"
              : "Publish Blog"}
        </button>
      </form>
    </div>
  );
};

export default AddBlogForm;
